import React from 'react';
import clsx from 'clsx';
import Alert from 'components/Alert';
import { FormikProps } from 'formik';
import { formatNumber, getCurrencySymbol } from 'utils/currency.helpers';
import t from 'utils/translationHelper';

import { DrawdownFundingMethod } from '@alpha/fx-dtos';
import {
  Box, Divider, FormControlLabel, Radio,
  RadioGroup, Typography,
} from '@alpha/ui-lib/ui/external';
import { StyledKeyValueTable, TKeyValue } from '@alpha/ui-lib/ui/table';

import { MassDrawdownRequestForm } from '../formData';

import { useStyles } from './index.styles';

interface IProps {
  drawdownForm: FormikProps<MassDrawdownRequestForm>;
  padAllowed: boolean;
  show: boolean;
  insufficientFunds: boolean;
  defaultAccountName: string,
  defaultAccountBalance: number,
  defaultAccountCurrencyCode: string,
}

const FundingMethod: React.FC<IProps> = ({
  drawdownForm,
  padAllowed = false,
  show = false,
  insufficientFunds,
  defaultAccountName,
  defaultAccountBalance,
  defaultAccountCurrencyCode,
}) => {
  const styles = useStyles();

  const tableData = (fundingMethod: DrawdownFundingMethod): TKeyValue[] => {
    const data: TKeyValue[] = [];

    if (fundingMethod === DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE) {
      data.push(
        {
          key: `${t('account_name')}:`,
          value: defaultAccountName || '-',
        },
        {
          key: `${t('account_balance')}:`,
          value: `${getCurrencySymbol(defaultAccountCurrencyCode)}${formatNumber(defaultAccountBalance, 2, false, true) || '-'} `,
        },
      );
    }

    return data;
  };

  if (show) {
    return (
      <Box className={styles.card} style={{ marginTop: '25px' }}>
        <Box style={{ padding: '20px 20px 0px 20px' }}>
          <Typography style={{ fontWeight: '600', marginBottom: '10px', fontSize: '16px' }}>{t('funding_method')}</Typography>
          <RadioGroup
            row
            value={drawdownForm.values.fundingMethod}
            onChange={(e) => drawdownForm.setFieldValue('fundingMethod', e.target.value)}
            style={{
              flexDirection: 'column',
            }}
            id="drawdown-funding-method"
          >

            <FormControlLabel
              value={DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE}
              control={<Radio color="primary" />}
              label={t('use_funds_on_account')}
              data-testid="funds-on-account-drawdown"
              name="drawdown-funding-method-account"
            />

            <FormControlLabel
              value={DrawdownFundingMethod.WIRE_TRANSFER}
              control={<Radio color="primary" />}
              label={t('wire_transfer_funds')}
              data-testid="wire-transfer-drawdown"
              name="drawdown-funding-method-wired"
            />

            {padAllowed && (
            <FormControlLabel
              value={DrawdownFundingMethod.PAD}
              control={<Radio color="primary" />}
              label={t('settle_with_pad')}
              data-testid="pad-drawdown"
              name="drawdown-funding-method-pad"
            />
            )}
          </RadioGroup>
        </Box>

        {tableData(drawdownForm.values.fundingMethod).length > 0 && (
          <Box>
            <Divider />
            <Box style={{ padding: '15px', backgroundColor: '#f7f7f7' }}>
              <StyledKeyValueTable
                testId="trade-funding-method-table"
                data={tableData(drawdownForm.values.fundingMethod)}
                className={clsx({
                  [styles.insufficientFundsTable]: insufficientFunds
                    && drawdownForm.values.fundingMethod === DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE,
                  [styles.sufficientFundsTable]: !insufficientFunds
                    && drawdownForm.values.fundingMethod === DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE,
                })}
              />
            </Box>
          </Box>
        )}

        {drawdownForm.values.fundingMethod === DrawdownFundingMethod.WIRE_TRANSFER && (
          <div style={{ width: '100%', padding: '20px' }}>
            <Alert
              className={styles.alert}
              variant="info"
              text={t('details_of_where_to_send_the_settlement_will_be_displayed_on_the_final_screen')}
            />
          </div>
        )}

        {drawdownForm.values.fundingMethod === DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE
          && insufficientFunds && (
            <div style={{ width: '100%' }}>
              <Alert
                variant="warning"
                className={styles.insufficientFundsAlert}
                text={t('insufficient_funds')}
                subText={t('weve_noticed_insufficient_funds_to_perform_drawdowns')}
              />
            </div>
        )}

        {drawdownForm.values.fundingMethod === DrawdownFundingMethod.PAD
         && (
         <div style={{ width: '10px auto', padding: '20px' }}>
           <Alert
             variant="info"
             className={styles.alert}
             text={t('pad_approved_drawdowns')}
           />
         </div>
         )}
      </Box>
    );
  }

  return <> </>;
};

export default FundingMethod;
